
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosClient from "../api/axiosClient";
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider
} from "@mui/material";
import {
  Save,
  ArrowBack,
  Edit as EditIcon,
  Link as LinkIcon,
  Description
} from "@mui/icons-material";

export default function EditNote() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [description, setDesc] = useState("");
  const [reference_link, setReference] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchNote = async () => {
      try {
        setFetchLoading(true);
        const res = await axiosClient.get(`api/notes/${id}`);
        setTitle(res.data.title || "");
        setDesc(res.data.description || "");
        setReference(res.data.reference_link || "");
      } catch (err) {
        setError("Failed to load note. Please try again.");
      } finally {
        setFetchLoading(false);
      }
    };

    fetchNote();
  }, [id]);

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!title.trim() || !description.trim()) {
      setError("Title and Description are required");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const payload = { title, description, reference_link };
      await axiosClient.put(`api/notes/${id}`, payload);
      navigate(`/notes`);
    } catch (err) {
      setError("Failed to update note. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: "center" }}>
        <CircularProgress sx={{ color: "#3E1E68" }} />
        <Typography variant="h6" sx={{ mt: 2, color: "#3E1E68" }}>
          Loading note...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          // background: "linear-gradient(135deg, #3E1E68 0%, #5D2F77 100%)",
          background:"#2a1c32ff",
          color: "white",
          borderRadius: 3
        }}
      >
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            sx={{
              color: "white",
              mr: 2,
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.1)"
              }
            }}
          />
          <EditIcon sx={{ mr: 2, fontSize: 32 }} />
          <Typography variant="h4" component="h1" fontWeight="bold">
            Edit Note
          </Typography>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {/* Edit Form */}
        <Box component="form" onSubmit={handleUpdate} sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Title Field */}
          <TextField
            label="Title"
            variant="outlined"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "white",
                borderRadius: 2,
                "&:hover fieldset": {
                  borderColor: "#ffffff"
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#ffffff"
                }
              },
              "& .MuiInputLabel-root": {
                color: "white"
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "white"
              }
            }}
          />

          {/* Description Field */}
          <TextField
            label="Description"
            variant="outlined"
            value={description}
            onChange={(e) => setDesc(e.target.value)}
            required
            multiline
            rows={6}
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "white",
                borderRadius: 2,
                "&:hover fieldset": {
                  borderColor: "#ffffff"
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#ffffff"
                }
              },
              "& .MuiInputLabel-root": {
                color: "white"
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "white"
              }
            }}
            InputProps={{
              startAdornment: (
                <Description sx={{ color: "text.secondary", mr: 1, mt: 2, alignSelf: 'flex-start' }} />
              ),
            }}
          />

          {/* Reference Link Field */}
          <TextField
            label="Reference Link"
            variant="outlined"
            value={reference_link}
            onChange={(e) => setReference(e.target.value)}
            fullWidth
            placeholder="https://example.com"
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "white",
                borderRadius: 2,
                "&:hover fieldset": {
                  borderColor: "#ffffff"
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#ffffff"
                }
              },
              "& .MuiInputLabel-root": {
                color: "white"
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "white"
              }
            }}
            InputProps={{
              startAdornment: (
                <LinkIcon sx={{ color: "text.secondary", mr: 1 }} />
              ),
            }}
          />

          <Divider sx={{ backgroundColor: "rgba(255,255,255,0.3)", my: 2 }} />

          {/* Action Buttons */}
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                color: "white",
                borderColor: "white",
                px: 4,
                "&:hover": {
                  borderColor: "white",
                  backgroundColor: "rgba(255,255,255,0.1)"
                }
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <Save />}
              sx={{
                backgroundColor: "white",
                color: "#3E1E68",
                px: 4,
                fontWeight: "bold",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                  transform: "translateY(-2px)",
                  boxShadow: 3
                },
                "&:disabled": {
                  backgroundColor: "rgba(255,255,255,0.5)"
                }
              }}
            >
              {loading ? "Updating..." : "Update Note"}
            </Button>
          </Box>
        </Box>

        {/* Quick Tips */}
        <Box sx={{ mt: 4, p: 2, backgroundColor: "rgba(255,255,255,0.1)", borderRadius: 2 }}>
          <Typography variant="body2" sx={{ opacity: 0.9, fontStyle: "italic" }}>
            💡 Tip: Make sure your title is descriptive and your description contains all the important details you want to remember.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
}